/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  DatabaseElastic,
  SearchLocate,
  Tools,
  IbmWatsonDiscovery
} from '@carbon/react/icons';
import {
  ClickableTile,
  InlineNotification,
  Modal,
  Link
} from 'carbon-components-react';
import _ from 'lodash';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { generateDocsUrl } from '@ibm-watson/shared/modules/ia/util/docs';
import { useAppSelector } from '../../../../store';
import { PortalTypes } from '../../../../ts/portal';
import { setSearchSkillDraft } from '../../../discovery/store/reducer';
import {
  setElasticSearchSkillDraft,
  setIsEditing,
  setShowDetailConfig
} from '../../../elastic-search/store/reducer';
import { selectSearchSkillIdByEnvironment } from '../../store/environments/selectors';
import {
  selectIsCpdVersion4,
  selectIsMilvusEnabled
} from '../../store/features/selectors';
import { pushPortal, popPortal } from '../../store/portals/reducer';
import { selectSkillById } from '../../store/skills/core-selectors';
import { isWxoEnabled } from '../../util/config';
import { messages } from './messages';

import './SearchChoiceModal.scss';

export interface SearchChoiceModalProps {
  onClose(): void;
  environmentId: string;
}

const learnMoreLink = generateDocsUrl(
  '/watson-assistant?topic=watson-assistant-search-overview'
);

const SearchChoiceModal = ({
  onClose,
  environmentId
}: SearchChoiceModalProps) => {
  const formatMsg = useIntl().formatMessage;

  const dispatch = useDispatch();

  const skillId = useAppSelector(state =>
    selectSearchSkillIdByEnvironment(state, environmentId)
  );
  const isMilvusEnabled = useAppSelector(selectIsMilvusEnabled);
  const isCpdVersion4 = useAppSelector(selectIsCpdVersion4);

  const skill = useAppSelector(state => selectSkillById(state, skillId));
  const isEdit = !_.isEmpty(skill?.search_settings);
  const isEditingElastic = !_.isEmpty(skill?.search_settings?.elastic_search);
  const isEditingDiscovery = !_.isEmpty(skill?.search_settings?.discovery);
  const isEditingMilvus = !_.isEmpty(skill?.search_settings?.milvus);

  const onMilvusClick = useCallback(() => {
    dispatch(setElasticSearchSkillDraft({ skill: null, isDirty: false }));
    dispatch(popPortal());
    dispatch(
      pushPortal({
        type: PortalTypes.MILVUS_OVERLAY,
        props: {
          skillId,
          environmentId
        }
      })
    );
  }, [dispatch, environmentId, skillId]);

  const onElasticClick = useCallback(() => {
    // In case the user is switching back and forth between search skill types, clear the draft of the other type so
    // they aren't confusingly in the middle of configuring if they go back to the old type.
    dispatch(setSearchSkillDraft({ skill: null, isDirty: false }));
    // If this is an existing Elasticsearch search skill then set isEditing to true, otherwise if this is either a new
    // search skill or a Discovery search skill then set isEditing to false (which will force a password to be required
    // by ElasticSearchInstanceConfig.tsx).
    dispatch(setIsEditing(isEditingElastic));
    // We need to make sure show detail config is false when Elasticsearch tile is clicked. Otherwise it's possible the
    // user switched Assistants before closing the panel, in which case the Elasticsearch overlay would still think it
    // should show the detailed config.
    dispatch(setShowDetailConfig(false));
    dispatch(popPortal());
    dispatch(
      pushPortal({
        type: PortalTypes.ELASTIC_SEARCH_OVERLAY,
        props: {
          skillId,
          environmentId
        }
      })
    );
  }, [dispatch, environmentId, isEditingElastic, skillId]);

  const onDiscoClick = useCallback(() => {
    // In case the user is switching back and forth between search skill types, clear the draft of the other type so
    // they aren't confusingly in the middle of configuring if they go back to the old type.
    dispatch(setElasticSearchSkillDraft({ skill: null, isDirty: false }));
    dispatch(popPortal());
    dispatch(
      pushPortal({
        type: PortalTypes.DISCOVERY_SEARCH_OVERLAY,
        props: {
          skillId,
          environmentId
        }
      })
    );
  }, [dispatch, environmentId, skillId]);

  const onCustomServiceClick = useCallback(() => {
    // In case the user is switching back and forth between search skill types, clear the draft of the other type so
    // they aren't confusingly in the middle of configuring if they go back to the old type.
    dispatch(setSearchSkillDraft({ skill: null, isDirty: false }));
    dispatch(setElasticSearchSkillDraft({ skill: null, isDirty: false }));
    dispatch(popPortal());
    dispatch(
      pushPortal({
        type: PortalTypes.CUSTOM_SERVICE_OVERLAY,
        props: {
          skillId,
          environmentId
        }
      })
    );
  }, [dispatch, environmentId, skillId]);

  return (
    <Modal
      className="SearchChoiceModal"
      modalHeading={
        isEdit
          ? formatMsg(messages.modalEditTitle)
          : formatMsg(messages.modalTitle)
      }
      passiveModal
      onRequestClose={onClose}
      open
      size="md">
      {isEdit && (
        <InlineNotification
          className="SearchChoiceModal__editWarning"
          kind="warning"
          lowContrast={true}
          title={
            isEditingElastic
              ? formatMsg(messages.modalEditElasticsearch)
              : isEditingDiscovery
              ? formatMsg(messages.modalEditDiscovery)
              : isEditingMilvus
              ? formatMsg(messages.modalEditMilvus)
              : formatMsg(messages.modalEditCustomService)
          }
          subtitle={formatMsg(messages.modalEditWarning)}
          hideCloseButton={true}
        />
      )}
      <div className="SearchChoiceModal__tileContainer">
        <ClickableTile
          onClick={onElasticClick}
          className="SearchChoiceModal__tile"
          light={true}>
          <div className="SearchChoiceModal__tile--icon">
            <DatabaseElastic size={32} />
          </div>
          <div className="SearchChoiceModal__tile--title">
            {formatMsg(messages.elasticCardTitle)}
          </div>
          <div className="SearchChoiceModal__tile--description">
            {formatMsg(messages.elasticCardDescription)}
          </div>
          <div className="SearchChoiceModal__tile--footer">
            {formatMsg(messages.recommendedTag)}
          </div>
        </ClickableTile>
        {isMilvusEnabled && (
          <ClickableTile
            onClick={onMilvusClick}
            className="SearchChoiceModal__tile"
            light={true}>
            <div className="SearchChoiceModal__tile--icon">
              <SearchLocate size={32} />
            </div>
            <div className="SearchChoiceModal__tile--title">
              {formatMsg(messages.milvusCardTitle)}
            </div>
            <div className="SearchChoiceModal__tile--description">
              {formatMsg(messages.milvusCardDescription)}
            </div>
          </ClickableTile>
        )}
        {!isWxoEnabled && (
          <ClickableTile
            onClick={onDiscoClick}
            className="SearchChoiceModal__tile"
            light={true}>
            <IbmWatsonDiscovery size={32} />
            <div className="SearchChoiceModal__tile--title">
              {formatMsg(messages.watsonDiscoveryTitle)}
            </div>
            <div className="SearchChoiceModal__tile--description">
              {formatMsg(messages.watsonDiscoveryDescription)}
            </div>
          </ClickableTile>
        )}
        {!isCpdVersion4 && (
          <ClickableTile
            onClick={onCustomServiceClick}
            className="SearchChoiceModal__tile"
            light={true}>
            <Tools size={32} />
            <div className="SearchChoiceModal__tile--title">
              {formatMsg(messages.customServiceTitle)}
            </div>
            <div className="SearchChoiceModal__tile--description">
              {formatMsg(messages.customServiceDescription)}
              <Link
                href={learnMoreLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => {
                  e.stopPropagation();
                }}>
                {formatMsg(messages.learnMore)}
              </Link>
            </div>
          </ClickableTile>
        )}
      </div>
    </Modal>
  );
};

export { SearchChoiceModal };
